import React, { useState, useMemo, useCallback } from 'react';
import {
  missionaries as missionariesApi,
  missionaryPrayers as prayersApi,
  missionaryVisits as visitsApi
} from '../../api/endpoints';
import {
  useMultiLedger, usePagination, useNotification, useOptionList, useChurchConfig, useSearch,
  useOpenRecordFromLink, findLedgerRow, RECORD_GONE
} from '../../hooks';
import { formatDate, formatCurrency, todayISO } from '../../utils/format';
import useStyles from '../../theme/useStyles';
import {
  PageHeader, DataTable, Pagination, Notification, PromptDialog, KpiCard, FormLayout, Input, Select, SearchableSelect, Badge, Button, TablePanel, SearchBar, SubTabs, PrintButton
} from '../../components/ui';

// Sponsorship: a dashboard, a filterable directory, the registration
// form, a read-only profile sheet and an activity hub. Prayer requests and
// visit reports are separate tables rather than JSON blobs on the missionary,
// so they survive independently and can be queried.

const GENDERS = ['Male', 'Female'];
const MARITAL_STATUSES = ['Single', 'Married', 'Widowed'];
const MISSIONARY_STATUSES = ['Active', 'On Leave', 'Retired', 'Transferred'];
const PRIORITIES = ['Normal', 'High', 'Urgent'];
const REQUEST_STATUSES = ['Open', 'Answered', 'Closed'];

const EMPTY_MISSIONARY = {
  fullName: '', gender: 'Male', dob: '', maritalStatus: 'Single', mobileNumber: '', email: '',
  missionField: '', diocese: '', organization: '', ministryType: '', dateJoined: '',
  missionaryStatus: 'Active',
  permanentAddress: '', missionAddress: '', deployedState: '', deployedDistrict: '', deployedPin: ''
};

// Three filters, not nine. Organisation, ministry type, diocese, district and
// the joined-date range each matched about one row apiece on a directory this
// size, and the search bar below already covers field, organisation and name
// as free text (CHM-178).
const EMPTY_FILTERS = {
  missionField: '', status: 'Active', deployedState: ''
};

const EMPTY_PRAYER = { date: todayISO(), request: '', priority: 'Normal', requestStatus: 'Open' };
const EMPTY_VISIT = { date: '', service: '', topic: '', offering: '' };

const statusTone = (status?: string) =>
  status === 'Active' ? 'success'
  : status === 'Retired' || status === 'Transferred' ? 'neutral'
  : 'warning';

const priorityTone = (priority?: string) =>
  priority === 'Urgent' ? 'error' : priority === 'High' ? 'warning' : 'neutral';

/** Case-insensitive contains, tolerant of an unset field. */
const has = (value: unknown, needle: string) =>
  String(value || '').toLowerCase().includes(needle.toLowerCase());

/** Days until the next occurrence of a birthday, or null if there's no date. */
const daysToBirthday = (dob?: string): number | null => {
  if (!dob) return null;
  const parts = dob.split('-');
  if (parts.length < 3) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const next = new Date(today.getFullYear(), Number(parts[1]) - 1, Number(parts[2]));
  if (Number.isNaN(next.getTime())) return null;
  if (next < today) next.setFullYear(today.getFullYear() + 1);
  return Math.ceil((next.getTime() - today.getTime()) / 86400000);
};

const Missionaries = ({ isDarkMode }) => {
  const styles = useStyles(isDarkMode);
  const t = styles.t;

  const [viewMode, setViewMode] = useState('dashboard');
  const [form, setForm] = useState(EMPTY_MISSIONARY);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [editingId, setEditingId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [profileTab, setProfileTab] = useState('prayer');
  const [prayerForm, setPrayerForm] = useState(EMPTY_PRAYER);
  const [visitForm, setVisitForm] = useState(EMPTY_VISIT);

  const isEditing = editingId !== null;
  const { notification, notify, dismiss } = useNotification();
  const config = useChurchConfig();

  const ledger = useMultiLedger({
    missionaries: { resource: missionariesApi, label: 'Sponsorship', prefix: 'MSN' },
    prayers: { resource: prayersApi, label: 'Prayer Request', prefix: 'MPR' },
    visits: { resource: visitsApi, label: 'Visit Report', prefix: 'MVR' }
  }, { notify });

  const fields = useOptionList('erp_miss_fields');
  const orgs = useOptionList('erp_miss_orgs');
  const types = useOptionList('erp_miss_types');
  const dioceses = useOptionList('erp_miss_dioceses');
  const states = useOptionList('erp_states');
  const districts = useOptionList('erp_districts');

  const people = ledger.active('missionaries');

  // Every filter is ANDed — narrowing one never widens another.
  const activeDataset = useMemo(() => {
    let rows = people;
    if (filters.status && filters.status !== 'All') {
      rows = rows.filter((m) => m.missionaryStatus === filters.status);
    }
    if (filters.missionField) rows = rows.filter((m) => has(m.missionField, filters.missionField));
    if (filters.deployedState) rows = rows.filter((m) => has(m.deployedState, filters.deployedState));
    return rows;
  }, [people, filters]);

  // Free-text search on top of the filter panel: a name or phone number is
  // what most lookups start from.
  const { searchTerm, setSearchTerm, filtered: searched, clear: clearSearch } = useSearch(
    activeDataset, ['recordId', 'fullName', 'mobileNumber', 'email', 'missionField', 'organization', 'diocese']
  );
  const page = usePagination(searched);

  const prayersFor = useCallback(
    (recordId) => ledger.active('prayers').filter((p) => p.missionaryId === recordId),
    [ledger]
  );
  const visitsFor = useCallback(
    (recordId) => ledger.active('visits').filter((v) => v.missionaryId === recordId),
    [ledger]
  );

  const metrics = useMemo(() => ({
    total: people.length,
    active: people.filter((m) => m.missionaryStatus === 'Active').length,
    openPrayers: ledger.active('prayers').filter((p) => p.requestStatus === 'Open').length
  }), [people, ledger]);

  /** The age they are turning on this birthday — blank if the dob is unusable. */
  const turningAge = (dob?: string): number | null => {
    const born = new Date(String(dob || ''));
    if (Number.isNaN(born.getTime())) return null;
    const now = new Date();
    let age = now.getFullYear() - born.getFullYear();
    const passed = now.getMonth() > born.getMonth()
      || (now.getMonth() === born.getMonth() && now.getDate() >= born.getDate());
    if (!passed) age -= 1;
    return age + 1;
  };

  // Anyone with a birthday inside the next month, soonest first.
  const upcomingBirthdays = useMemo(
    () => people
      .map((m) => ({ person: m, days: daysToBirthday(m.dob) }))
      .filter((x) => x.days !== null && x.days <= 30)
      .sort((a, b) => (a.days as number) - (b.days as number)),
    [people]
  );

  const resetForm = () => { setForm(EMPTY_MISSIONARY); setEditingId(null); };

  const goToList = () => { setSelected(null); setViewMode('list'); };

  const openRegister = () => { resetForm(); setSelected(null); setViewMode('add'); };
  const openView = (m) => { setSelected(m); setViewMode('view'); };
  const openProfile = (m) => { setSelected(m); setProfileTab('prayer'); setViewMode('profile'); };

  // "Open the record" from the Approvals inbox: a sponsorship opens in its
  // View; a prayer request or visit report in its missionary's profile.
  useOpenRecordFromLink(({ id, ref }) => {
    const row = findLedgerRow(ledger.rows, id, ref) as (typeof ledger.rows)[number] & { missionaryId?: string } | null;
    if (!row) { notify(RECORD_GONE, 'error'); return; }
    if (row.__key === 'missionaries') { openView(row); return; }
    const owner = ledger.rows.find((r) => r.__key === 'missionaries'
      && (r as { recordId?: string }).recordId === row.missionaryId);
    if (!owner) { notify(RECORD_GONE, 'error'); return; }
    openProfile(owner);
    setProfileTab(row.__key === 'visits' ? 'visits' : 'prayer');
  }, !ledger.loading);

  const openEdit = (m) => {
    setForm({
      fullName: m.fullName || '', gender: m.gender || 'Male', dob: m.dob || '',
      maritalStatus: m.maritalStatus || 'Single', mobileNumber: m.mobileNumber || '', email: m.email || '',
      missionField: m.missionField || '', diocese: m.diocese || '', organization: m.organization || '',
      ministryType: m.ministryType || '', dateJoined: m.dateJoined || '',
      missionaryStatus: m.missionaryStatus || 'Active',
      permanentAddress: m.permanentAddress || '', missionAddress: m.missionAddress || '',
      deployedState: m.deployedState || '', deployedDistrict: m.deployedDistrict || '',
      deployedPin: m.deployedPin || ''
    });
    setEditingId(m.rawId);
    setSelected(m);
    setViewMode('add');
  };

  const toggleSelection = (id: number) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));

  const pageIds = page.currentItems.map((m) => m.rawId as number);
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));

  const toggleSelectAllOnPage = (checked: boolean) =>
    setSelectedIds((prev) => (checked
      ? Array.from(new Set([...prev, ...pageIds]))
      : prev.filter((id) => !pageIds.includes(id))));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const fullName = form.fullName.trim();
    if (!fullName) { notify('The name is required.', 'error'); return; }
    if (form.mobileNumber && !/^\d{10}$/.test(form.mobileNumber)) {
      notify('Mobile number must be exactly 10 digits.', 'error');
      return;
    }

    if (form.missionField.trim()) fields.ensure(form.missionField.trim());
    if (form.organization.trim()) orgs.ensure(form.organization.trim());
    if (form.ministryType.trim()) types.ensure(form.ministryType.trim());
    if (form.diocese.trim()) dioceses.ensure(form.diocese.trim());
    if (form.deployedState.trim()) states.ensure(form.deployedState.trim());
    if (form.deployedDistrict.trim()) districts.ensure(form.deployedDistrict.trim());

    const existing = isEditing ? people.find((m) => m.rawId === editingId) : null;
    const payload = {
      ...form,
      fullName,
      recordId: existing?.recordId || ledger.nextSequence('missionaries'),
      status: 'ACTIVE' as const
    };

    if (await ledger.save('missionaries', payload, editingId)) {
      resetForm();
      goToList();
    }
  };

  const handleAddPrayer = async (e) => {
    e.preventDefault();
    if (!selected) return;
    if (!prayerForm.request.trim()) { notify('The prayer request is required.', 'error'); return; }
    const payload = {
      ...prayerForm,
      missionaryId: selected.recordId,
      recordId: ledger.nextSequence('prayers'),
      status: 'ACTIVE' as const
    };
    if (await ledger.save('prayers', payload)) setPrayerForm(EMPTY_PRAYER);
  };

  const handleAddVisit = async (e) => {
    e.preventDefault();
    if (!selected) return;
    if (!visitForm.date) { notify('The visit date is required.', 'error'); return; }
    const payload = {
      ...visitForm,
      offering: visitForm.offering ? parseFloat(visitForm.offering) : 0,
      missionaryId: selected.recordId,
      recordId: ledger.nextSequence('visits'),
      status: 'ACTIVE' as const
    };
    if (await ledger.save('visits', payload)) setVisitForm(EMPTY_VISIT);
  };

  /** Prints the ticked rows, or the whole filtered directory when none are ticked. */

  const setFilter = (key: string) => (value: string) =>
    { setFilters((prev) => ({ ...prev, [key]: value })); page.reset(); };

  const columns = [
    {
      key: 'select',
      header: (
        <input
          type="checkbox"
          checked={allOnPageSelected}
          onChange={(e) => toggleSelectAllOnPage(e.target.checked)}
          aria-label="Select all on this page"
          style={{ cursor: 'pointer' }}
        />
      ),
      width: '40px',
      render: (r) => (
        <input
          type="checkbox"
          checked={selectedIds.includes(r.rawId)}
          onChange={() => toggleSelection(r.rawId)}
          aria-label={`Select ${r.fullName}`}
          style={{ cursor: 'pointer' }}
        />
      )
    },
    { key: 'recordId', header: 'ID', nowrap: true, render: (r) => <strong>{r.recordId}</strong> },
    {
      key: 'fullName',
      header: 'Name',
      render: (r) => (
        <>
          <strong>{r.fullName}</strong>
          <div style={{ fontSize: '11px', opacity: 0.7 }}>
            {r.gender} · Joined {formatDate(r.dateJoined)}
          </div>
        </>
      )
    },
    {
      key: 'missionField',
      header: 'Mission Field & Org',
      render: (r) => (
        <>
          <div>{r.missionField || '-'}</div>
          <div style={{ fontSize: '11px', opacity: 0.7 }}>{r.organization || '-'}</div>
        </>
      )
    },
    { key: 'ministryType', header: 'Ministry Type', render: (r) => <Badge tone="neutral">{r.ministryType || '-'}</Badge> },
    { key: 'mobileNumber', header: 'Mobile Number', nowrap: true, render: (r) => r.mobileNumber || '-' },
    {
      key: 'location',
      header: 'Location',
      render: (r) => [r.deployedDistrict, r.deployedState].filter(Boolean).join(', ') || '-'
    },
    {
      key: 'missionaryStatus',
      header: 'Status',
      render: (r) => <Badge tone={statusTone(r.missionaryStatus)}>{r.missionaryStatus || 'Active'}</Badge>
    },
    { key: 'createdBy', header: 'Created By', render: (r) => (r as { createdBy?: string }).createdBy || '-' },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right' as const,
      render: (r) => (
        <div style={{ display: 'flex', gap: '5px', justifyContent: 'flex-end' }}>
          <Button variant="secondary" size="sm" onClick={() => openView(r)}>View</Button>
          <Button size="sm" onClick={() => openProfile(r)}>Profile</Button>
          <Button write variant="ghost" size="sm" onClick={() => openEdit(r)}>Edit</Button>
          <Button variant="danger" size="sm" style={styles.deleteBtn} onClick={() => ledger.requestVoid(r)}>Delete</Button>
        </div>
      )
    }
  ];

  const panel = {
    backgroundColor: t.bgSubtle,
    border: `1px solid ${t.border}`,
    borderRadius: t.radius.lg,
    padding: t.spacing.lg,
    marginBottom: t.spacing.lg
  };

  const sectionTitle = {
    margin: `0 0 ${t.spacing.md} 0`,
    color: t.textAccent,
    fontSize: t.fontSize.lg,
    borderBottom: `1px solid ${t.border}`,
    paddingBottom: t.spacing.sm
  };

  const backBar = (label: string, subtitle?: string) => (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      gap: t.spacing.md, flexWrap: 'wrap', marginBottom: t.spacing.lg
    }}>
      <div>
        <h2 style={{ margin: 0, color: t.textMain }}>{label}</h2>
        {subtitle && <div style={{ color: t.textMuted, fontSize: t.fontSize.md }}>{subtitle}</div>}
      </div>
      <Button variant="secondary" onClick={goToList}>← Back to Directory</Button>
    </div>
  );

  const row = (label: string, value?: React.ReactNode) => (
    <div style={{
      display: 'flex', justifyContent: 'space-between', gap: t.spacing.md,
      padding: '9px 0', borderBottom: `1px solid ${t.borderDashed}`
    }}>
      <span style={{ color: t.textMuted, fontSize: t.fontSize.md }}>{label}</span>
      <span style={{ color: t.textMain, fontSize: t.fontSize.md, fontWeight: 'bold', textAlign: 'right' }}>
        {value || '-'}
      </span>
    </div>
  );

  return (
    <div style={{ ...styles.container, minHeight: '80vh' }}>
      <Notification notification={notification} onClose={dismiss} />

      <PageHeader icon="🌍" title="Sponsorship" />

      {/* Top-level views. Hidden while a single missionary is open, since those
          screens have their own Back control. */}
      {['dashboard', 'list', 'add'].includes(viewMode) && (
        <SubTabs
          value={viewMode}
          onChange={(k) => {
            if (k === 'add') openRegister();
            else { setViewMode(k); if (k === 'list') page.reset(); }
          }}
          tabs={[
            { key: 'dashboard', label: 'Mission dashboard' },
            { key: 'list', label: 'Sponsorship directory' },
            { key: 'add', write: true, label: isEditing ? '✎ Editing sponsorship…' : '+ Add new entry' }
          ]}
        />
      )}

      {viewMode === 'dashboard' && (
        <>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: t.spacing.md,
            marginBottom: t.spacing.lg
          }}>
            <KpiCard label="Total Supported" value={String(metrics.total)} color={t.info} />
            <KpiCard label="Active on Field" value={String(metrics.active)} color={t.income} />
            <KpiCard label="Pending Prayer Requests" value={String(metrics.openPrayers)} color={t.warning} />
          </div>

          <div style={panel}>
            <h3 style={sectionTitle}>🎂 Upcoming Birthdays (next 30 days)</h3>
            {upcomingBirthdays.length === 0 ? (
              <p style={{ color: t.textFaint, margin: 0 }}>No birthdays upcoming.</p>
            ) : (
              upcomingBirthdays.map(({ person, days }) => (
                <div
                  key={person.rawId}
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                    gap: '12px', padding: '10px 0', borderBottom: `1px solid ${t.borderDashed}`
                  }}
                >
                  {/* Name and a countdown alone were not enough to act on —
                      the date, the age being turned, where they serve and how
                      to reach them all belong here (CHM-161). */}
                  <div>
                    <strong style={{ color: t.textMain }}>{person.fullName}</strong>
                    <div style={{ fontSize: t.fontSize.sm, color: t.textMuted }}>
                      {[
                        formatDate(person.dob),
                        turningAge(person.dob) !== null ? `turning ${turningAge(person.dob)}` : '',
                        person.gender
                      ].filter(Boolean).join('  ·  ')}
                    </div>
                    <div style={{ fontSize: t.fontSize.sm, color: t.textFaint }}>
                      {[
                        person.missionField || 'Mission field not set',
                        [person.deployedDistrict, person.deployedState].filter(Boolean).join(', ')
                      ].filter(Boolean).join('  ·  ')}
                    </div>
                    <div style={{ fontSize: t.fontSize.sm, color: t.textFaint }}>
                      {[person.mobileNumber, person.email].filter(Boolean).join('  ·  ') || 'No contact on record'}
                    </div>
                  </div>
                  <Badge tone={days === 0 ? 'success' : 'neutral'}>
                    {days === 0 ? 'Today' : `in ${days} day${days === 1 ? '' : 's'}`}
                  </Badge>
                </div>
              ))
            )}
          </div>
        </>
      )}

      {viewMode === 'list' && (
        <>
          <div style={panel}>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginBottom: t.spacing.md, flexWrap: 'wrap', gap: t.spacing.sm
            }}>
              <h3 style={{ margin: 0, color: t.textMain, fontSize: t.fontSize.lg }}>Filter Directory</h3>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button variant="secondary" size="sm" onClick={() => { setFilters(EMPTY_FILTERS); page.reset(); }}>
                  Clear Filters
                </Button>
              </div>
            </div>

            <FormLayout.Grid columns={3}>
              <Select
                label="Status" placeholder="" options={['All', ...MISSIONARY_STATUSES]}
                value={filters.status} onChange={(e) => setFilter('status')(e.target.value)}
              />
              <SearchableSelect
                label="Mission Field" placeholder="Any field"
                value={filters.missionField} onChange={setFilter('missionField')}
                options={fields.options} onOptionsChange={fields.setOptions}
              />
              <SearchableSelect
                label="Deployed State" placeholder="Any state"
                value={filters.deployedState} onChange={setFilter('deployedState')}
                options={states.options} onOptionsChange={states.setOptions}
              />
            </FormLayout.Grid>
          </div>

          <SearchBar
            value={searchTerm}
            onChange={(v) => { setSearchTerm(v); page.reset(); }}
            onClear={() => { clearSearch(); page.reset(); }}
            placeholder="Search by name, phone, email, field, organisation or ID..."
          />

          <TablePanel
            title="Missionary Sponsorships"
            below={<Pagination {...page} itemsOnPage={page.currentItems.length} label="sponsorships" />}
            columns={columns}
            rows={page.currentItems}
            rowKey="rawId"
            loading={ledger.loading}
            emptyMessage="No sponsorships match these filters."
          />
        </>
      )}

      {viewMode === 'add' && (
        <FormLayout
          title={isEditing ? 'Edit Sponsorship Profile' : 'Register Sponsorship'}
          submitLabel={isEditing ? 'Update Profile' : 'Save Sponsorship'}
          submitting={ledger.saving}
          onSubmit={handleSubmit}
          onCancel={goToList}
          cancelLabel="Cancel"
        >
          <FormLayout.Section title="Personal Information" />
          <FormLayout.Grid>
            <Input
              label="Full Name" required placeholder="e.g. Samuel David"
              value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })}
            />
            <Select
              label="Gender" placeholder="" options={GENDERS}
              value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })}
            />
            <Input
              label="Date of Birth" type="date"
              value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })}
            />
            <Select
              label="Marital Status" placeholder="" options={MARITAL_STATUSES}
              value={form.maritalStatus} onChange={(e) => setForm({ ...form, maritalStatus: e.target.value })}
            />
            <Input
              label="Mobile Number" maxLength={10} placeholder="10 digits"
              value={form.mobileNumber}
              onChange={(e) => setForm({ ...form, mobileNumber: e.target.value.replace(/\D/g, '') })}
            />
            <Input
              label="Email" type="email" placeholder="name@example.com"
              value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value.toLowerCase() })}
            />
          </FormLayout.Grid>

          <FormLayout.Section title="Ministry Deployment" />
          <FormLayout.Grid>
            <SearchableSelect
              label="Mission Field" placeholder="Search or type field..."
              value={form.missionField}
              onChange={(v) => setForm((prev) => ({ ...prev, missionField: v }))}
              options={fields.options} onOptionsChange={fields.setOptions}
            />
            <SearchableSelect
              label="Organisation" placeholder="Search or type organisation..."
              value={form.organization}
              onChange={(v) => setForm((prev) => ({ ...prev, organization: v }))}
              options={orgs.options} onOptionsChange={orgs.setOptions}
            />
            <SearchableSelect
              label="Ministry Type" placeholder="Search or type ministry..."
              value={form.ministryType}
              onChange={(v) => setForm((prev) => ({ ...prev, ministryType: v }))}
              options={types.options} onOptionsChange={types.setOptions}
            />
            <SearchableSelect
              label="Diocese" placeholder="Search or type diocese..."
              value={form.diocese}
              onChange={(v) => setForm((prev) => ({ ...prev, diocese: v }))}
              options={dioceses.options} onOptionsChange={dioceses.setOptions}
            />
            <Input
              label="Date Joined" type="date"
              value={form.dateJoined} onChange={(e) => setForm({ ...form, dateJoined: e.target.value })}
            />
            <Select
              label="Status" placeholder="" options={MISSIONARY_STATUSES}
              value={form.missionaryStatus}
              onChange={(e) => setForm({ ...form, missionaryStatus: e.target.value })}
            />
          </FormLayout.Grid>

          <FormLayout.Section title="Address & Location" />
          <FormLayout.Grid>
            <Input
              label="Permanent Address" type="textarea" rows={2} fullWidth
              value={form.permanentAddress}
              onChange={(e) => setForm({ ...form, permanentAddress: e.target.value })}
            />
            <Input
              label="Mission Field Address" type="textarea" rows={2} fullWidth
              value={form.missionAddress}
              onChange={(e) => setForm({ ...form, missionAddress: e.target.value })}
            />
            <SearchableSelect
              label="Deployed State" placeholder="Search or type state..."
              value={form.deployedState}
              onChange={(v) => setForm((prev) => ({ ...prev, deployedState: v }))}
              options={states.options} onOptionsChange={states.setOptions}
            />
            <SearchableSelect
              label="Deployed District" placeholder="Search or type district..."
              value={form.deployedDistrict}
              onChange={(v) => setForm((prev) => ({ ...prev, deployedDistrict: v }))}
              options={districts.options} onOptionsChange={districts.setOptions}
            />
            <Input
              label="PIN Code" maxLength={6} placeholder="e.g. 629001"
              value={form.deployedPin}
              onChange={(e) => setForm({ ...form, deployedPin: e.target.value.replace(/\D/g, '') })}
            />
          </FormLayout.Grid>
        </FormLayout>
      )}

      {viewMode === 'view' && selected && (
        <>
          {backBar(selected.fullName, `${selected.recordId} · ${selected.organization || 'Independent'}`)}

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: t.spacing.md }}>
            <PrintButton
              module="Missionaries"
              documentType="MISSIONARY_PROFILE"
              data={() => ({
                ...selected,
                name: selected.fullName,
                dateOfBirth: formatDate(selected.dob),
                dob: formatDate(selected.dob),
                dateJoined: formatDate(selected.dateJoined),
                location: [selected.deployedDistrict, selected.deployedState].filter(Boolean).join(', ')
              })}
              title={`Missionary-${selected.recordId || selected.id}`}
              onNotify={notify}
              isDarkMode={isDarkMode}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: t.spacing.lg }}>
            <div style={panel}>
              <h3 style={sectionTitle}>Personal Information</h3>
              {row('Full Name', selected.fullName)}
              {row('Gender', selected.gender)}
              {row('Date of Birth', formatDate(selected.dob))}
              {row('Marital Status', selected.maritalStatus)}
              {row('Mobile Number', selected.mobileNumber)}
              {row('Email', selected.email)}
            </div>

            <div style={panel}>
              <h3 style={sectionTitle}>Ministry Deployment</h3>
              {row('Mission Field', selected.missionField)}
              {row('Organisation', selected.organization)}
              {row('Ministry Type', selected.ministryType)}
              {row('Diocese', selected.diocese)}
              {row('Date Joined', formatDate(selected.dateJoined))}
              {row('Status', <Badge tone={statusTone(selected.missionaryStatus)}>{selected.missionaryStatus || 'Active'}</Badge>)}
            </div>

            <div style={panel}>
              <h3 style={sectionTitle}>Address &amp; Location</h3>
              {row('Permanent Address', selected.permanentAddress)}
              {row('Mission Address', selected.missionAddress)}
              {row('State', selected.deployedState)}
              {row('District', selected.deployedDistrict)}
              {row('PIN Code', selected.deployedPin)}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px', marginTop: t.spacing.lg }}>
            <Button onClick={() => openProfile(selected)}>Open Activity Profile →</Button>
            <Button write variant="ghost" onClick={() => openEdit(selected)}>Edit Profile</Button>
          </div>
        </>
      )}

      {viewMode === 'profile' && selected && (
        <>
          {backBar(selected.fullName, `${selected.recordId} · ${selected.missionField || 'Mission field not set'}`)}

          <SubTabs
            value={profileTab}
            onChange={setProfileTab}
            tabs={[
              { key: 'prayer', label: `Prayer requests (${prayersFor(selected.recordId).length})` },
              { key: 'visits', label: `Church visits (${visitsFor(selected.recordId).length})` },
              { key: 'reports', label: 'Mission reports' }
            ]}
          />

          {profileTab === 'prayer' && (
            <>
              <form onSubmit={handleAddPrayer} style={panel}>
                <FormLayout.Grid columns={3}>
                  <Input
                    label="Date" type="date"
                    value={prayerForm.date} onChange={(e) => setPrayerForm({ ...prayerForm, date: e.target.value })}
                  />
                  <Select
                    label="Priority" placeholder="" options={PRIORITIES}
                    value={prayerForm.priority} onChange={(e) => setPrayerForm({ ...prayerForm, priority: e.target.value })}
                  />
                  <Select
                    label="Status" placeholder="" options={REQUEST_STATUSES}
                    value={prayerForm.requestStatus}
                    onChange={(e) => setPrayerForm({ ...prayerForm, requestStatus: e.target.value })}
                  />
                  <Input
                    label="Request" type="textarea" rows={2} fullWidth required
                    placeholder="What to pray for..."
                    value={prayerForm.request} onChange={(e) => setPrayerForm({ ...prayerForm, request: e.target.value })}
                  />
                </FormLayout.Grid>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: t.spacing.sm }}>
                  <Button type="submit" size="sm" disabled={ledger.saving}>+ Add Request</Button>
                </div>
              </form>

              <DataTable
                columns={[
                  { key: 'date', header: 'Date', nowrap: true, render: (r) => formatDate(r.date) },
                  { key: 'priority', header: 'Priority', render: (r) => <Badge tone={priorityTone(r.priority)}>{r.priority}</Badge> },
                  { key: 'request', header: 'Request' },
                  {
                    key: 'requestStatus',
                    header: 'Status',
                    render: (r) => <Badge tone={r.requestStatus === 'Open' ? 'warning' : 'success'}>{r.requestStatus}</Badge>
                  },
                  {
                    key: 'actions',
                    header: 'Actions',
                    align: 'right' as const,
                    render: (r) => <Button variant="danger" size="sm" style={styles.deleteBtn} onClick={() => ledger.requestVoid(r)}>Delete</Button>
                  }
                ]}
                rows={prayersFor(selected.recordId)}
                rowKey="rawId"
                emptyMessage="No requests recorded."
              />
            </>
          )}

          {profileTab === 'visits' && (
            <>
              <form onSubmit={handleAddVisit} style={panel}>
                <FormLayout.Grid columns={3}>
                  <Input
                    label="Date" type="date" required
                    value={visitForm.date} onChange={(e) => setVisitForm({ ...visitForm, date: e.target.value })}
                  />
                  <Input
                    label="Service" placeholder="e.g. Sunday Morning"
                    value={visitForm.service} onChange={(e) => setVisitForm({ ...visitForm, service: e.target.value })}
                  />
                  <Input
                    label="Offering Received (₹)" type="number" step="0.01" placeholder="0.00"
                    value={visitForm.offering} onChange={(e) => setVisitForm({ ...visitForm, offering: e.target.value })}
                    style={{ color: t.income, fontWeight: 'bold' }}
                  />
                  <Input
                    label="Topic / Message" fullWidth placeholder="e.g. The Great Commission"
                    value={visitForm.topic} onChange={(e) => setVisitForm({ ...visitForm, topic: e.target.value })}
                  />
                </FormLayout.Grid>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: t.spacing.sm }}>
                  <Button type="submit" size="sm" disabled={ledger.saving}>+ Add Visit</Button>
                </div>
              </form>

              <DataTable
                columns={[
                  { key: 'date', header: 'Date', nowrap: true, render: (r) => formatDate(r.date) },
                  {
                    key: 'service',
                    header: 'Service & Topic',
                    render: (r) => (<><div>{r.service || '-'}</div><div style={{ fontSize: '11px', opacity: 0.7 }}>{r.topic || '-'}</div></>)
                  },
                  {
                    key: 'offering',
                    header: 'Offering Recd.',
                    align: 'right' as const,
                    render: (r) => <span style={{ color: t.income, fontWeight: 'bold' }}>{formatCurrency(r.offering)}</span>
                  },
                  {
                    key: 'actions',
                    header: 'Actions',
                    align: 'right' as const,
                    render: (r) => <Button variant="danger" size="sm" style={styles.deleteBtn} onClick={() => ledger.requestVoid(r)}>Delete</Button>
                  }
                ]}
                rows={visitsFor(selected.recordId)}
                rowKey="rawId"
                emptyMessage="No visits recorded."
              />
            </>
          )}

          {profileTab === 'reports' && (
            <div style={{ ...panel, textAlign: 'center', padding: '40px' }}>
              <div style={{ fontSize: '48px' }}>📄</div>
              <h3 style={{ color: t.textMain, margin: `${t.spacing.sm} 0` }}>Mission Field Reports</h3>
              <p style={{ color: t.textFaint, margin: 0 }}>
                Nothing is stored here yet — uploading and tracking PDF field reports
                needs file storage, which is not wired up.
              </p>
            </div>
          )}
        </>
      )}

      <PromptDialog
        open={Boolean(ledger.voiding)}
        title={`Delete ${ledger.voiding?.recordType || ''} ${ledger.voiding?.recordId || ''}`}
        message="Please enter the reason for removing this record. It will be kept in the voided log."
        placeholder="Reason for voiding"
        confirmLabel="Delete"
        requiredError="A reason is required to remove a record."
        onSubmit={(reason) => ledger.confirmVoid(reason)}
        onCancel={ledger.cancelVoid}
      />
    </div>
  );
};

export default Missionaries;
